import { adminImpersonationApi } from '@/api/admin/adminImpersonationApi';
import { userService } from '@/api/user/userApi';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useAuthStore } from '@/store/authStore';
import { handleGlobalError } from '@/utils/error-handler';
import { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Permanent notice that the current session is borrowed.
 *
 * Not decoration and not dismissible: while impersonating there is no ADMIN
 * role, so the Backoffice section is gone from the sidebar and this banner is
 * the only way back. It is also the only thing standing between "I am looking
 * at a client's account" and "I think this is mine".
 *
 * Fixed to the bottom of the viewport rather than the top: the Header is
 * `fixed ... top-0`, so a banner placed above it in normal flow would render
 * underneath it and be invisible. Anchoring `top-0` instead would require
 * making the Header's offset and the page container's `pt-16` conditional on
 * an active support session. Bottom-fixed avoids that, at the price of the
 * page and the two floating bars having to clear it — which they do through
 * `--support-banner-h` below, not by each knowing this component exists.
 *
 * Rendered above `<Routes>` in App, not inside Layout: `/tv/view` is a
 * top-level route outside the Layout element, so from inside it the banner
 * would simply vanish — taking with it both the notice that this is somebody
 * else's account and the only way out, since the borrowed token carries no
 * ADMIN role and the sidebar therefore has no Backoffice section. It is also
 * why this cannot live inside ManagerGuard: that guard and the TermsGuard it
 * composes render a screen *instead of* their children.
 */
export const ImpersonationBanner = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const impersonation = useAuthStore((state) => state.impersonation);
  const upgradeImpersonation = useAuthStore((state) => state.upgradeImpersonation);
  const stopImpersonation = useAuthStore((state) => state.stopImpersonation);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // The bar is `fixed`, so it is out of flow and nothing underneath it knows it
  // is there. Publishing its height on the root is what lets every
  // bottom-anchored element clear it with one `var()`, instead of each one
  // subscribing to the store and repeating the same guessed number — which is
  // how the offset came to live in three files at once, with the next sticky
  // element born hidden behind the bar and nobody the wiser.
  //
  // Measured rather than constant: the bar wraps to two or three lines on a
  // narrow screen, and a constant is wrong there by exactly the height that
  // swallows a button.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const element = bannerRef.current;
    if (!element) {
      root.style.removeProperty('--support-banner-h');
      return;
    }
    const publish = () =>
      root.style.setProperty('--support-banner-h', `${element.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--support-banner-h');
    };
  }, [impersonation]);

  if (!impersonation) return null;

  const { actingAs, takeover, canTakeOver } = impersonation;
  // See `ImpersonationState.canTakeOver` for why undefined counts as allowed.
  const isTakeoverAllowed = canTakeOver !== false;
  const modeText = takeover
    ? '. Lo que hagas se ejecuta como esa cuenta.'
    : isTakeoverAllowed
      ? '. Modo lectura.'
      : '. Modo lectura: la cuenta está suspendida o eliminada y no se puede tomar el control.';

  const takeControl = async () => {
    setIsUpgrading(true);
    try {
      const result = await adminImpersonationApi.takeControl(actingAs.pk);
      upgradeImpersonation({
        accessToken: result.accessToken,
        takeover: result.takeover,
      });
      toast({
        title: 'Control tomado',
        description: `Lo que hagas ahora se ejecuta como ${actingAs.email} y sale de verdad: mails y notificaciones incluidos.`,
      });
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudo tomar el control',
        'Volvé a intentarlo desde el backoffice.',
      );
      return;
    } finally {
      setIsUpgrading(false);
    }

    // The read-only session may have carried an OWNER role lent for viewing;
    // the takeover token runs with the account's real roles. Re-read them
    // without a loading screen, and outside the try above: control was already
    // taken, so a failure here must not be reported as a failed takeover.
    try {
      await userService.reloadCurrentUserInPlace();
    } catch (error) {
      handleGlobalError(
        error,
        'No se pudieron actualizar los permisos de la cuenta',
        'Recargá la página para verla con sus permisos reales.',
      );
    }
  };

  const exit = () => {
    stopImpersonation();
    navigate('/admin/torneos', { replace: true });
  };

  return (
    <div
      ref={bannerRef}
      className={`fixed bottom-0 left-0 right-0 z-50 flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-sm text-white ${
        takeover ? 'border-red-900 bg-red-700' : 'border-amber-800 bg-amber-600'
      }`}
    >
      <span className="min-w-0 break-words">
        {takeover ? 'Control tomado sobre ' : 'Viendo como '}
        <strong>{actingAs.name || actingAs.email}</strong>
        {actingAs.name ? ` (${actingAs.email})` : ''}
        {modeText}
      </span>
      <span className="flex flex-shrink-0 gap-2">
        {!takeover && isTakeoverAllowed && (
          <Button size="sm" variant="secondary" onClick={takeControl} disabled={isUpgrading}>
            {isUpgrading ? 'Tomando control...' : 'Tomar control'}
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={exit}>
          Salir
        </Button>
      </span>
    </div>
  );
};
